import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createClientFromRequest,
  fetchPendingClientRequests,
  PendingClientRequestDto,
  PendingClientRequestsResponseDto,
} from './useApi';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_INDEX = 1;

const PendingClientRequests = () => {
  const [requests, setRequests] = useState<PendingClientRequestDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [pageSizeInput, setPageSizeInput] = useState(String(DEFAULT_PAGE_SIZE));
  const [pageIndexInput, setPageIndexInput] = useState(String(DEFAULT_PAGE_INDEX));
  const [password, setPassword] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageIndex, setPageIndex] = useState(DEFAULT_PAGE_INDEX);
  const [totalCount, setTotalCount] = useState(0);
  const [processingRequestIds, setProcessingRequestIds] = useState<string[]>([]);
  const [completedRequestIds, setCompletedRequestIds] = useState<string[]>([]);

  useEffect(() => {
    let isMounted = true;

    if (!password) {
      setRequests([]);
      setTotalCount(0);
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    async function loadRequests() {
      setLoading(true);
      setError(null);

      try {
        const response: PendingClientRequestsResponseDto = await fetchPendingClientRequests(
          pageSize,
          pageIndex,
          password
        );

        if (isMounted) {
          setRequests(response.data);
          setTotalCount(response.totalCount);
        }
      } catch (loadError: any) {
        if (isMounted) {
          setError(loadError.message || 'Failed to load pending client requests.');
          setRequests([]);
          setTotalCount(0);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadRequests();

    return () => {
      isMounted = false;
    };
  }, [pageIndex, pageSize, password]);

  const applyPagination = () => {
    const nextPassword = passwordInput.trim();
    const nextPageSize = Number(pageSizeInput);
    const nextPageIndex = Number(pageIndexInput);

    if (!nextPassword) {
      setError('Edit pass is required.');
      return;
    }

    if (!Number.isInteger(nextPageSize) || nextPageSize <= 0) {
      setError('PageSize must be a positive integer.');
      return;
    }

    if (!Number.isInteger(nextPageIndex) || nextPageIndex <= 0) {
      setError('PageIndex must be a positive integer.');
      return;
    }

    setError(null);
    setPassword(nextPassword);
    setPageSize(nextPageSize);
    setPageIndex(nextPageIndex);
  };

  const handleCreateClient = async (clientRequestId: string, verified: boolean) => {
    setError(null);
    setProcessingRequestIds((currentIds) => [...currentIds, clientRequestId]);

    try {
      await createClientFromRequest(clientRequestId, password, verified);
      setCompletedRequestIds((currentIds) => [...currentIds, clientRequestId]);
    } catch (requestError: any) {
      setError(requestError.message || 'Failed to create client from request.');
    } finally {
      setProcessingRequestIds((currentIds) =>
        currentIds.filter((currentId) => currentId !== clientRequestId)
      );
    }
  };

  return (
    <Box className="f4k-page pending-page">
      <Box className="f4k-panel pending-panel">
      <Typography variant="h5" className="f4k-title" sx={{ mb: 2 }}>
        Pending Client Requests
      </Typography>

      <Typography variant="body1" className="f4k-subtitle" sx={{ mb: 2 }}>
        Total Count: {totalCount}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <TextField
          label="Edit pass"
          type="password"
          value={passwordInput}
          onChange={(event) => setPasswordInput(event.target.value)}
          size="small"
        />
        <TextField
          label="Page Size"
          type="number"
          value={pageSizeInput}
          onChange={(event) => setPageSizeInput(event.target.value)}
          inputProps={{ min: 1 }}
          size="small"
        />
        <TextField
          label="Page Index"
          type="number"
          value={pageIndexInput}
          onChange={(event) => setPageIndexInput(event.target.value)}
          inputProps={{ min: 1 }}
          size="small"
        />
        <Button
          variant="contained"
          onClick={applyPagination}
          disabled={loading}
          sx={{
            backgroundColor: 'var(--f4k-brand-600)',
            '&:hover': { backgroundColor: 'var(--f4k-brand-700)' },
          }}
        >
          Load
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        sx={{
          border: '1px solid var(--f4k-border)',
          boxShadow: 'var(--f4k-shadow)',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'var(--f4k-bg-150)' }}>
              <TableRow>
                <TableCell>clientRequestID</TableCell>
                <TableCell>name</TableCell>
                <TableCell>description</TableCell>
                <TableCell>latitude</TableCell>
                <TableCell>longitude</TableCell>
                <TableCell>clientTypeName</TableCell>
                <TableCell>website</TableCell>
                <TableCell>email</TableCell>
                <TableCell>Contact Email</TableCell>
                <TableCell>phone</TableCell>
                <TableCell>createdDate</TableCell>
                <TableCell>clientRequestFeatures</TableCell>
                <TableCell>action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    No pending client requests found.
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.clientRequestID} hover>
                    <TableCell>{request.clientRequestID}</TableCell>
                    <TableCell>{request.name}</TableCell>
                    <TableCell>{request.description}</TableCell>
                    <TableCell>{request.latitude}</TableCell>
                    <TableCell>{request.longitude}</TableCell>
                    <TableCell>{request.clientTypeName}</TableCell>
                    <TableCell>{request.website || '-'}</TableCell>
                    <TableCell>{request.email || '-'}</TableCell>
                    <TableCell>{request.contactEmail || '-'}</TableCell>
                    <TableCell>{request.phone || '-'}</TableCell>
                    <TableCell>{new Date(request.createdDate).toLocaleString()}</TableCell>
                    <TableCell>
                      {request.clientRequestFeatures.map((feature) => feature.name).join(', ')}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleCreateClient(request.clientRequestID, true)}
                          disabled={
                            processingRequestIds.includes(request.clientRequestID) ||
                            completedRequestIds.includes(request.clientRequestID)
                          }
                          sx={{
                            backgroundColor: 'var(--f4k-brand-600)',
                            '&:hover': { backgroundColor: 'var(--f4k-brand-700)' },
                          }}
                        >
                          {processingRequestIds.includes(request.clientRequestID)
                            ? 'Creating...'
                            : completedRequestIds.includes(request.clientRequestID)
                              ? 'Created'
                              : 'Create Verified Client'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleCreateClient(request.clientRequestID, false)}
                          disabled={
                            processingRequestIds.includes(request.clientRequestID) ||
                            completedRequestIds.includes(request.clientRequestID)
                          }
                          sx={{
                            borderColor: 'var(--f4k-trust-600)',
                            color: 'var(--f4k-trust-600)',
                            '&:hover': {
                              borderColor: 'var(--f4k-brand-700)',
                              color: 'var(--f4k-brand-700)',
                            },
                          }}
                        >
                          {processingRequestIds.includes(request.clientRequestID)
                            ? 'Creating...'
                            : completedRequestIds.includes(request.clientRequestID)
                              ? 'Created'
                              : 'Create Unverified Client'}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </Box>
    </Box>
  );
};

export default PendingClientRequests;